// BrainPanel — expanded popover from the brain pill.
// Shows active brains (X to remove) + available brains (+ to add).
// Calls PATCH /api/threads/:id/brain_ids when active set changes.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Brain, Thread } from '../types';
import { ElementCell } from './ElementCell';

interface Props {
  thread: Thread;
  allBrains: Brain[];
  onChange: (newBrainIds: string[]) => Promise<void>;
  onClose: () => void;
}

export function BrainPanel({ thread, allBrains, onChange, onClose }: Props) {
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-outside-to-close
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    // Defer one tick so the pill click that opened us doesn't immediately close us
    const t = setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [onClose]);

  const activeIds = useMemo(() => new Set(thread.brain_ids), [thread.brain_ids]);
  const active = useMemo(
    () => thread.brain_ids.map((id) => allBrains.find((b) => b.id === id)).filter((b): b is Brain => !!b),
    [thread.brain_ids, allBrains],
  );
  const available = useMemo(
    () =>
      allBrains
        .filter((b) => !activeIds.has(b.id))
        .filter((b) =>
          search.trim() === ''
            ? true
            : (b.name + b.tags + b.description).toLowerCase().includes(search.toLowerCase()),
        ),
    [allBrains, activeIds, search],
  );

  async function remove(brainId: string) {
    const next = thread.brain_ids.filter((id) => id !== brainId);
    if (next.length === 0) return; // Don't allow empty (API would reject anyway)
    setPending(true);
    try { await onChange(next); } finally { setPending(false); }
  }
  async function add(brainId: string) {
    const next = [...thread.brain_ids, brainId];
    setPending(true);
    try { await onChange(next); } finally { setPending(false); }
  }

  return (
    <div
      ref={ref}
      className="absolute top-14 left-10 w-[480px] bg-surface border border-border rounded-lg shadow-lg z-20 overflow-hidden"
      style={{ filter: pending ? 'opacity(0.6)' : undefined }}
    >
      {/* tiny pointer arrow */}
      <span className="absolute -top-2 left-8 w-3.5 h-3.5 bg-surface border-t border-l border-border rotate-45" />

      <div className="px-5 pt-4 pb-3 border-b border-hairline">
        <div className="flex justify-between items-center mb-1">
          <span className="font-display text-[10px] text-text2 uppercase tracking-wide12">Brains for this thread</span>
        </div>
        <div className="font-display text-sm text-ink">
          {active.length} active · locked for this thread
        </div>
        <div className="text-xs text-text2 mt-1 leading-snug">
          Element IQ picked these from your context. <span className="text-ink font-semibold">×</span> remove · <span className="text-ink font-semibold">+</span> add to bring more in.
        </div>
      </div>

      <div className="p-2 max-h-96 overflow-y-auto">
        {active.length > 0 && (
          <>
            <SectionLabel>Active in this thread</SectionLabel>
            {active.map((b) => (
              <BrainRow key={b.id} brain={b} action="remove" onClick={() => remove(b.id)} />
            ))}
          </>
        )}

        {available.length > 0 && (
          <>
            <SectionLabel className={active.length > 0 ? 'border-t border-hairline mt-2 pt-3' : ''}>
              Available to add
            </SectionLabel>
            {available.map((b) => (
              <BrainRow key={b.id} brain={b} action="add" onClick={() => add(b.id)} muted />
            ))}
          </>
        )}

        {available.length === 0 && search.trim() !== '' && (
          <div className="text-text3 text-xs italic text-center py-6">No brains match "{search}".</div>
        )}
      </div>

      <div className="relative px-4 py-2 border-t border-hairline bg-bgsoft">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search brain library…"
          className="w-full bg-surface border border-hairline rounded pl-7 pr-2.5 py-1.5 font-sans text-xs text-ink outline-none focus:border-ink"
        />
        <span className="absolute left-7 top-1/2 -translate-y-1/2 w-3 h-3 border-[1.5px] border-text3 rounded-full pointer-events-none" />
      </div>
    </div>
  );
}

function SectionLabel({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`font-display text-[10px] text-text3 uppercase tracking-wide12 px-3 pt-3 pb-1.5 ${className}`}>
      {children}
    </div>
  );
}

function BrainRow({ brain, action, onClick, muted = false }: { brain: Brain; action: 'remove' | 'add'; onClick: () => void; muted?: boolean }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-bgsoft transition-colors">
      <ElementCell symbol={brain.symbol} atomicNumber={brain.atomic_number} size="md" className={muted ? 'opacity-50' : ''} />
      <div className="flex-1 min-w-0 flex flex-col leading-snug">
        <span className={`text-[13px] font-medium ${muted ? 'text-text3' : 'text-ink'} truncate`}>{brain.name}</span>
        <span className="font-mono text-[10px] text-text3 mt-0.5">{brain.org} · v{brain.version} · {brain.author}</span>
      </div>
      <button
        onClick={onClick}
        className={`bg-transparent border border-border rounded px-2 py-1 font-mono text-[11px] text-text2 hover:text-ink transition-colors ${
          action === 'remove' ? 'hover:border-accent hover:text-accent' : 'hover:border-ink'
        }`}
      >
        {action === 'remove' ? '× remove' : '+ add'}
      </button>
    </div>
  );
}
